import React, {useEffect, useState} from "react";
import {Box, Chip, IconButton} from "@material-ui/core";
import Email from "@material-ui/icons/Email";
import {useCurrentShow, useUserConversations} from "hooks";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import messagingService from "api/messaging";
import FlexBox from "view/components/Layout/FlexBox";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {IExhibitorPair, ShowExhibitorChatMode} from "@evennt-tech/api-v2-core";
import {useCurrentExhibitor} from "store/current-exhibitor/current-exhibitor.store";
import {shallow} from "zustand/shallow";
import {useSearchParams} from "react-router-dom";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import Conversation from "../Conversation";
import {useStyles} from "./style";

const nameNotFound = "NAME NOT FOUND";

type ChatProps = {
  showId: string;
  userId: string;
  userRole: string;
  exhibitorPair: IExhibitorPair;
};

const Chat = ({
  showId,
  userId,
  userRole,

  exhibitorPair,
}: ChatProps) => {
  const classes = useStyles();
  const t = useLocalizedNameTranslate();
  const chatTitle = t
    ? t("exhibitor.chat.chat_with_us", "Chat with Us")
    : "Chat with Us";
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [conversationWasOpened, setConversationWasOpened] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const {conversations} = useUserConversations(showId, exhibitorPair);
  const [fullName, setFullName] = useState<string>(nameNotFound);
  const [contractorCompanyName, setContractorCompanyName] = useState<string>(
    ""
  );
  const [show] = useCurrentShow();

  const localMap = useLocalizedLocalMap(show, exhibitorPair?.stand?.builder);

  const {fetch, autoCatch} = useCurrentExhibitor();

  const infoAboutCurrentExhibitor = useCurrentExhibitor(
    (state) => state.getAll(),
    shallow
  );

  useEffect(() => {
    const load = async () => {
      await fetch();
      await autoCatch();
    };

    load();
  }, []);

  useEffect(() => {
    const openChant = searchParams.get("openChant");

    if (openChant) {
      setIsChatOpen(true);

      searchParams.delete("openChant");
      setSearchParams(searchParams, {replace: true});
    }
  }, [searchParams]);

  useEffect(() => {
    setFullName(infoAboutCurrentExhibitor?.user?.name || nameNotFound);

    const exhibitorConnection = (
      infoAboutCurrentExhibitor?.connections || []
    ).find(({exhibitorPairId}) => exhibitorPairId === exhibitorPair.id);

    setContractorCompanyName(exhibitorConnection?.contractorCompanyName || "");
  }, [infoAboutCurrentExhibitor, setFullName]);

  const getSelectedConversation = () => {
    if (exhibitorPair?.stand) {
      return conversations?.[exhibitorPair.stand.standId]?.organisers;
    }
    return null;
  };

  useEffect(() => {
    messagingService.updateLastSeenExhibitor(
      showId,
      exhibitorPair!.stand!.standId,
      userId
    );
  }, [showId, exhibitorPair, userId, conversations]);

  const unread =
    conversations?.[exhibitorPair!.stand!.standId]?.organisers?.unreadCount ||
    0;

  const chatContent = isChatOpen ? (
    <Box
      className={clsx(classes.chatContent, {
        [classes.chatContentHidden]: !isChatOpen,
      })}
    >
      <Box className={classes.header}>
        <FlexBox>
          <Email className={classes.emailIcon} />
          <Box className={classes.btnTitle}>{chatTitle}</Box>
        </FlexBox>
        <IconButton size="small" onClick={() => setIsChatOpen(false)}>
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Conversation
        localizedExhibitorLabel={localMap.exhibitor}
        showId={showId}
        userId={userId}
        userRole={userRole}
        organiserId={userId}
        standId={exhibitorPair!.stand!.standId}
        selectedConversation={exhibitorPair!.stand!.standId}
        conversationData={getSelectedConversation()}
        fullName={fullName}
        contractorCompanyName={contractorCompanyName}
        conversationWasChanged={conversationWasOpened}
        localizedOrganiserLabel={localMap.organiser}
      />
    </Box>
  ) : null;

  if (
    !showId ||
    show?.exhibitorChatMode !== ShowExhibitorChatMode.MessagingBoard
  ) {
    return null;
  }

  return (
    <div>
      <Box
        className={`${classes.bubble} ${
          isChatOpen ? classes.bubbleOpen : classes.bubbleClosed
        }`}
        onClick={() => {
          if (!isChatOpen) {
            setIsChatOpen(true);
            setConversationWasOpened(true);
            setTimeout(() => {
              setConversationWasOpened(false);
            }, 3000);
          }
        }}
      >
        {isChatOpen ? (
          chatContent
        ) : (
          <FlexBox alignCenter>
            <FlexBox
              alignCenter
              marginRight="16px"
              className={classes.btnTitle}
            >
              <Email
                className={clsx(classes.emailIcon, {
                  [classes.emailIconHidden]: isChatOpen,
                })}
              />
              {chatTitle}{" "}
            </FlexBox>
            {unread > 0 && (
              <Chip
                className={classes.badge}
                color="secondary"
                size="small"
                label={unread}
              />
            )}
          </FlexBox>
        )}
      </Box>
    </div>
  );
};

export default Chat;
